import { Fragment, ReactNode, useMemo } from 'react';

import { CAST_PILOT_ASSISTANT } from '@cast/constants';
import {
  Button,
  type CodeElementProps,
  Icons,
  PreviewMarkdownCodeElement,
  notify,
  Typography,
  Box,
  tokens,
} from '@cast/design-system';
import type { ChatMessage, ChatMessageArtifact } from '@cast/types';
import type { Components } from 'react-markdown';

import { ChatMarkdown } from './ChatMarkdown';
import { FollowUpQuestions } from './FollowUpQuestions';
import { RechartsArtifact } from './RechartsArtifact';
import { classifyLinkDestination, trackOpsPilot } from './_analytics';
import { useChatTracking } from './useChatTracking';

const markdownLinkColor = tokens.theme.fg.action.primary;

const ARTIFACT_RENDERERS: Partial<
  Record<
    ChatMessageArtifact['type'],
    (artifact: ChatMessageArtifact) => ReactNode
  >
> = {
  recharts_chart: (artifact) => <RechartsArtifact config={artifact.chart} />,
};

type MessageBubbleProps = {
  message: ChatMessage;
  isLatest: boolean;
  pendingRetry?: boolean;
  onRetry?: () => void;
  onFollowUpClick?: (command: string) => void;
};

export const MessageBubble = ({
  message,
  isLatest,
  pendingRetry,
  onRetry,
  onFollowUpClick,
}: MessageBubbleProps) => {
  const isUser = message.role === 'user';
  const author = isUser ? 'You' : CAST_PILOT_ASSISTANT;
  const { context_id, organization_id } = useChatTracking();

  const markdownComponents = useMemo(
    (): Components => ({
      a: ({ href, children }) => (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          style={{ color: markdownLinkColor }}
          onClick={() => {
            // Only track for agent answers; user messages rarely contain
            // links and would skew the click-through signal.
            if (isUser || !href) return;
            trackOpsPilot.answerLinkClicked({
              context_id,
              organization_id,
              destination: classifyLinkDestination(href),
              href,
            });
          }}
        >
          {children}
        </a>
      ),
      code: (props) => (
        <PreviewMarkdownCodeElement
          {...(props as CodeElementProps)}
          onCopy={() => {
            notify.success('Copied to clipboard');
            if (!isUser) {
              trackOpsPilot.answerCopied({
                context_id,
                organization_id,
              });
            }
          }}
        />
      ),
    }),
    [isUser, context_id, organization_id]
  );

  return (
    <Box
      sx={{
        p: isUser ? 8 : 0,
        border: isUser
          ? `1px solid ${tokens.theme.border.structural.neutral}`
          : 'none',
        borderRadius: '8px',
        background: isUser
          ? tokens.theme.bg.structural.surfaceRaised
          : tokens.theme.bg.structural.surface,
        maxWidth: isUser ? '90%' : '100%',
        width: isUser ? undefined : '100%',
        minWidth: '100px',
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        wordBreak: 'break-word',
      }}
    >
      <Typography color={tokens.theme.fg.structural.secondary} variant="P10B">
        {author}:
      </Typography>
      <ChatMarkdown text={message.text} components={markdownComponents} />
      {message.artifacts?.map((artifact, index) => (
        <Fragment key={index}>
          {ARTIFACT_RENDERERS[artifact.type]?.(artifact)}
        </Fragment>
      ))}
      {!isUser && !!message.followUps?.length ? (
        <FollowUpQuestions
          followUps={message.followUps}
          onFollowUpClick={onFollowUpClick}
        />
      ) : null}
      {pendingRetry && isLatest && onRetry && (
        <Button
          variant="text"
          size="sm"
          onClick={onRetry}
          startIcon={<Icons.ArrowClockwise />}
          data-testid="chat-retry-button"
          sx={{ mt: 4 }}
        >
          Retry
        </Button>
      )}
    </Box>
  );
};
